import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { useEdgeAlignedDropdown } from "../../hooks/useEdgeAlignedDropdown";

export interface SelectOption {
    value: string;
    label: string;
}

interface SelectProps {
    value: string;
    onChange: (value: string) => void;
    options: SelectOption[];
    className?: string;
    disabled?: boolean;
    /** Full-width form-field style (h-11, justify-between) instead of the
     * compact toolbar-chip style (h-8) used for inline filter bars. */
    fullWidth?: boolean;
}

// Drop-in, design-system-styled replacement for a native <select> — native
// <option> lists can't be restyled cross-browser, so filters that need to
// match the app's look (selected-state highlight, rounded panel, etc.) use
// this instead.
export function Select({ value, onChange, options, className = "", disabled = false, fullWidth = false }: SelectProps) {
    const [open, setOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const dropdownAlign = useEdgeAlignedDropdown<HTMLDivElement>(open);
    const selected = options.find((o) => o.value === value);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <div ref={containerRef} className={`relative ${fullWidth ? "w-full" : ""}`}>
            <button
                type="button"
                disabled={disabled}
                onClick={() => setOpen((o) => !o)}
                className={`${fullWidth ? "h-11 w-full justify-between pl-3 pr-3 text-sm rounded-lg" : "h-8 pl-2.5 pr-2 text-xs rounded-md"} border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 ${
                    className || "bg-surface border-border hover:border-border-strong"
                }`}
            >
                <span className={className ? "" : "text-ink"}>{selected?.label ?? ""}</span>
                <ChevronDown className={`w-3.5 h-3.5 shrink-0 ${className ? "opacity-70" : "text-ink-faint"} transition-transform duration-150 ${open ? "rotate-180" : ""}`} />
            </button>

            {open && (
                <div
                    ref={dropdownAlign.ref}
                    role="listbox"
                    className={`absolute top-full mt-1 ${fullWidth ? "inset-x-0 rounded-lg" : `${dropdownAlign.alignClass} min-w-full w-max rounded-2xl`} bg-surface border border-border shadow-float z-50 max-h-72 overflow-y-auto overflow-x-hidden ds-scrollbar py-1`}
                >
                    {options.map((option) => (
                        <button
                            key={option.value}
                            type="button"
                            role="option"
                            aria-selected={option.value === value}
                            onClick={() => {
                                onChange(option.value);
                                setOpen(false);
                            }}
                            className={`w-full text-left px-3 transition-colors duration-150 ${fullWidth ? "py-2 text-sm" : "py-1.5 text-xs"} ${
                                option.value === value
                                    ? "bg-ink text-ink-inverse"
                                    : "text-ink-muted hover:bg-surface-sunken"
                            }`}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
